/**
 * 공사일지 검색의 사용자 입력이 외부 분석·오류 도구로 새지 않게 URL에서 값을 가린다 (TRD §9.2).
 *
 * 대상은 검색 경로 두 곳뿐이다. 다른 URL은 그대로 둬 오류 수집에 필요한 정보를 잃지 않는다.
 * - 프로젝트 목록 화면 `/projects/{id}?q=…`
 * - 검색 API `…/projects/{id}/daily-logs/search?keyword=…&cursor=…`
 */

export const REDACTED_VALUE = "[Filtered]";

const PROJECT_PAGE_SENSITIVE_PARAMS = new Set(["q"]);
const SEARCH_API_SENSITIVE_PARAMS = new Set(["keyword", "cursor"]);

/** 검색 API 경로 + 쿼리스트링 (공백·따옴표·해시에서 끝난다) */
const SEARCH_API_URL = /(\/projects\/\d+\/daily-logs\/search\/?\?)([^\s"'#]*)/g;
/** 프로젝트 목록 화면 경로 + 쿼리스트링. `/projects/1/log/…` 같은 하위 경로는 제외된다 */
const PROJECT_PAGE_URL = /(\/projects\/\d+\/?\?)([^\s"'#]*)/g;

function redactQueryString(queryString: string, sensitiveParams: ReadonlySet<string>): string {
  return queryString
    .split("&")
    .map((pair) => {
      const separatorIndex = pair.indexOf("=");
      const rawName = separatorIndex < 0 ? pair : pair.slice(0, separatorIndex);
      let name = rawName;
      try {
        name = decodeURIComponent(rawName.replace(/\+/g, " "));
      } catch {
        // 잘못 인코딩된 이름은 원문으로 비교한다.
      }
      return sensitiveParams.has(name) ? `${rawName}=${REDACTED_VALUE}` : pair;
    })
    .join("&");
}

/** 문자열 안의 검색 URL에서 민감한 파라미터 값만 가린다. 여러 URL이 섞인 문자열도 처리한다 */
export function redactSearchUrl(value: string): string {
  return value
    .replace(
      SEARCH_API_URL,
      (_, path: string, query: string) => path + redactQueryString(query, SEARCH_API_SENSITIVE_PARAMS),
    )
    .replace(
      PROJECT_PAGE_URL,
      (_, path: string, query: string) => path + redactQueryString(query, PROJECT_PAGE_SENSITIVE_PARAMS),
    );
}

/** 경로와 분리된 쿼리스트링(예: Sentry `request.query_string`)을 URL 기준으로 가린다 */
export function redactSearchQueryString(url: string, queryString: string): string {
  if (/\/projects\/\d+\/daily-logs\/search/.test(url)) {
    return redactQueryString(queryString.replace(/^\?/, ""), SEARCH_API_SENSITIVE_PARAMS);
  }
  if (/\/projects\/\d+\/?(?:[?#]|$)/.test(url)) {
    return redactQueryString(queryString.replace(/^\?/, ""), PROJECT_PAGE_SENSITIVE_PARAMS);
  }
  return queryString;
}

const MAX_REDACT_DEPTH = 8;

/**
 * 객체 안의 모든 문자열에 redactSearchUrl을 적용한 사본을 만든다.
 * Sentry event·breadcrumb·span처럼 URL이 여러 필드(description, data.url, request.url…)에 흩어진 구조에 쓴다.
 */
export function redactSearchUrlsDeep<T>(value: T, depth = 0): T {
  if (typeof value === "string") return redactSearchUrl(value) as T;
  if (depth >= MAX_REDACT_DEPTH || value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((item) => redactSearchUrlsDeep(item, depth + 1)) as T;

  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    result[key] = redactSearchUrlsDeep(item, depth + 1);
  }
  return result as T;
}
